/**
 * @module Binding hooks for the Tiptap prompt editor.
 *
 * Ported from t3code's composer rich-text layer (ticket 3526, upstream pin
 * e3e7cc3f, MIT license retained in LICENSE): the editor instance plus
 * controller composition, the citation actions, and the orchestrator binding
 * (imperative handle).
 */

import { useEditor, type Editor } from "@tiptap/react";
import type { AssistantCitation, ServerProviderSkill } from "@cmmd-forge/contracts";
import { serializeAssistantCitation } from "@cmmd-forge/shared/assistantCitations";
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type Ref,
} from "react";
import { collapseExpandedComposerCursor } from "~/composer-logic";
import type { SkillMeta } from "~/composer-rich-text-doc";
import { buildComposerEditorOptions } from "./composerPromptEditorConfig";
import { resolveSkillMeta } from "./composerPromptEditorChips";
import { useComposerControlledSync, useComposerEditorState } from "./composerPromptEditorHooks";
import type { AssistantCitationSourceAnchor } from "~/lib/assistantTextSelection";

import type { TerminalContextDraft } from "~/lib/terminalContext";
import { removeComposerCitation, updateComposerCitationComment } from "./ComposerCitationNode";
import type { ComposerPromptEditorController } from "./composerPromptEditorController";
import { isCaretOnVisualEdge } from "./composerPromptEditorKeys";
import type { ComposerPromptEditorHandle } from "./ComposerPromptEditorTiptap";

interface ComposerCitationActions {
  readonly commentContext: {
    openComment: {
      citeKey: string;
      sourceAnchor?: AssistantCitationSourceAnchor;
      removeOnCancel?: boolean;
    } | null;
    onOpenChange: (citeKey: string, open: boolean) => void;
    onSubmitAndSend: () => void;
  };
  readonly editorState: {
    isEditable: () => boolean;
    updateCitationComment: (citeKey: string, comment: string) => boolean;
    removeCitation: (citeKey: string) => void;
  };
  readonly insertCitation: (
    citation: AssistantCitation,
    sourceAnchor?: AssistantCitationSourceAnchor,
  ) => boolean;
}

/** Comment panel state, editor-backed chip actions, and the insert seam. */
function useComposerCitationActions(input: {
  readonly editorHolder: React.RefObject<Editor | null>;
  readonly syncFromEditor: (editor: Editor) => void;
}): ComposerCitationActions {
  const { editorHolder, syncFromEditor } = input;
  const [openCitation, setOpenCitation] = useState<{
    citeKey: string;
    sourceAnchor?: AssistantCitationSourceAnchor;
    removeOnCancel?: boolean;
  } | null>(null);

  const commentContext = useMemo(
    () => ({
      openComment: openCitation,
      onOpenChange: (citeKey: string, open: boolean) => {
        setOpenCitation((current) => {
          if (open) return current?.citeKey === citeKey ? current : { citeKey };
          return current?.citeKey === citeKey ? null : current;
        });
      },
      onSubmitAndSend: () => {
        editorHolder.current?.view.dom.closest("form")?.requestSubmit();
      },
    }),
    [editorHolder, openCitation],
  );

  const editorState = useMemo(
    () => ({
      isEditable: () => editorHolder.current?.isEditable ?? false,
      updateCitationComment: (citeKey: string, comment: string): boolean => {
        const instance = editorHolder.current;
        if (!instance) return false;
        const accepted = updateComposerCitationComment(instance, citeKey, comment);
        if (accepted) syncFromEditor(instance);
        return accepted;
      },
      removeCitation: (citeKey: string) => {
        const instance = editorHolder.current;
        if (instance) removeComposerCitation(instance, citeKey);
      },
    }),
    [editorHolder, syncFromEditor],
  );

  const insertCitation = useCallback(
    (citation: AssistantCitation, sourceAnchor?: AssistantCitationSourceAnchor): boolean => {
      const instance = editorHolder.current;
      if (!instance || !instance.isEditable) return false;
      const citeKey = `citation-${Math.random().toString(36).slice(2)}`;
      instance
        .chain()
        .focus()
        .insertContent([
          {
            type: "composer-citation",
            attrs: { citation, source: serializeAssistantCitation(citation), citeKey },
          },
          { type: "text", text: " " },
        ])
        .run();
      setOpenCitation({ citeKey, ...(sourceAnchor ? { sourceAnchor } : {}), removeOnCancel: true });
      return true;
    },
    [editorHolder],
  );

  return { commentContext, editorState, insertCitation };
}

/**
 * The imperative handle the ChatComposer orchestrator drives: focus,
 * snapshots, and the visual-edge test prompt history uses.
 */
function useComposerPromptEditorHandle(input: {
  readonly editorRef: Ref<ComposerPromptEditorHandle>;
  readonly editor: Editor | null;
  readonly controller: ComposerPromptEditorController;
  readonly readSnapshot: () => {
    value: string;
    cursor: number;
    expandedCursor: number;
    terminalContextIds: string[];
  };
}): void {
  const { editorRef, editor, controller, readSnapshot } = input;
  useImperativeHandle(
    editorRef,
    () => ({
      focus: () => {
        if (editor) controller.focusAt(editor, controller.snapshotRef.current.cursor);
      },
      focusAt: (nextCursor: number) => {
        if (editor) controller.focusAt(editor, nextCursor);
      },
      focusAtEnd: () => {
        if (editor) {
          const end = controller.snapshotRef.current.value.length;
          controller.focusAt(
            editor,
            collapseExpandedComposerCursor(controller.snapshotRef.current.value, end),
          );
        }
      },
      readSelectionRange: () => {
        readSnapshot();
        return { ...controller.selectionRangeRef.current };
      },
      readSnapshot,
      isCaretOnVisualEdge: (edge) =>
        isCaretOnVisualEdge(readSnapshot(), editor?.view.dom ?? null, edge),
    }),
    [controller, editor, readSnapshot],
  );
}

interface EditorBindingInput {
  readonly editorRef: Ref<ComposerPromptEditorHandle>;
  readonly editor: Editor | null;
  readonly controller: ComposerPromptEditorController;
  readonly editorHolder: React.RefObject<Editor | null>;
  readonly richText: boolean;
  readonly value: string;
  readonly cursor: number;
  readonly terminalContexts: ReadonlyArray<TerminalContextDraft>;
  readonly skills: ReadonlyArray<ServerProviderSkill>;
  readonly disabled: boolean;
}

/**
 * The editor's binding to the orchestrator: store sync, the imperative
 * handle, and the citation actions, composed so the component reads as
 * mount, bind, render.
 */
export function useComposerPromptEditorBinding(input: EditorBindingInput): {
  readonly commentContext: ReturnType<typeof useComposerCitationActions>["commentContext"];
  readonly editorState: ReturnType<typeof useComposerCitationActions>["editorState"];
  readonly insertCitation: ReturnType<typeof useComposerCitationActions>["insertCitation"];
  readonly readSnapshot: () => {
    value: string;
    cursor: number;
    expandedCursor: number;
    terminalContextIds: string[];
  };
} {
  const { editor, controller } = input;
  useComposerControlledSync({
    editor,
    controller,
    value: input.value,
    cursor: input.cursor,
    richText: input.richText,
    terminalContexts: input.terminalContexts,
    skills: input.skills,
  });
  const readSnapshot = () => controller.readSnapshot(editor);
  useComposerPromptEditorHandle({
    editorRef: input.editorRef,
    editor,
    controller,
    readSnapshot,
  });
  const citations = useComposerCitationActions({
    editorHolder: input.editorHolder,
    syncFromEditor: controller.syncFromEditor,
  });
  // Publish the live editor instance for the imperative closures, and mirror
  // the disabled flag onto the ProseMirror view.
  useEffect(() => {
    input.editorHolder.current = input.editor;
    input.editor?.setEditable(!input.disabled);
  }, [input.disabled, input.editor, input.editorHolder]);
  return { ...citations, readSnapshot };
}

/**
 * The editor instance plus its controlled-state controller: the config is
 * built once at mount and the live callbacks reach it through the refs.
 */
export function useComposerTiptapEditor(input: {
  readonly initialValue: string;
  readonly initialCursor: number;
  readonly initialTerminalContextIds: ReadonlyArray<string>;
  readonly onChange: (
    nextValue: string,
    nextCursor: number,
    expandedCursor: number,
    cursorAdjacentToMention: boolean,
    terminalContextIds: string[],
  ) => void;
  readonly onCommandKeyDown:
    | ((
        key: "ArrowDown" | "ArrowUp" | "Enter" | "Tab",
        event: KeyboardEvent,
        editorValue: string,
      ) => boolean)
    | undefined;
  readonly onRemoveTerminalContext: (contextId: string) => void;
  readonly skills: ReadonlyArray<ServerProviderSkill>;
  readonly value: string;
  readonly cursor: number;
  readonly richText: boolean;
  readonly terminalContexts: ReadonlyArray<TerminalContextDraft>;
  readonly disabled: boolean;
  readonly placeholder: string;
  readonly className?: string;
}): {
  readonly editor: Editor | null;
  readonly editorHolder: React.RefObject<Editor | null>;
  readonly controller: ComposerPromptEditorController;
  readonly isEmpty: boolean;
} {
  const state = useComposerEditorState(input);
  const editorHolder = useRef<Editor | null>(null);
  const { onCommandKeyDownRef, onRemoveTerminalContextRef } = state;
  const skillLabelFor = (name: string): SkillMeta =>
    resolveSkillMeta(state.skillsRef.current, name);
  const editor = useEditor(
    buildComposerEditorOptions({
      getEditor: () => editorHolder.current,
      onEditorChange: state.controller.syncFromEditor,
      richText: input.richText,
      value: input.value,
      terminalContexts: input.terminalContexts,
      skillLabelFor,
      disabled: input.disabled,
      placeholder: input.placeholder,
      ...(input.className !== undefined ? { className: input.className } : {}),
      onCommandKeyDown: (
        key: "ArrowDown" | "ArrowUp" | "Enter" | "Tab",
        event: KeyboardEvent,
        editorValue: string,
      ) => onCommandKeyDownRef.current?.(key, event, editorValue) ?? false,
      onRemoveTerminalContext: (contextId: string) => onRemoveTerminalContextRef.current(contextId),
    }),
    [],
  );
  return {
    editor,
    editorHolder,
    controller: state.controller,
    isEmpty: state.isEmpty,
  };
}
